/**
 * buildReviewPeriods / buildUnitHalves
 *
 * Builds the shared client-offer-review-v2 period axis at a caller-selected
 * granularity, then re-buckets each unit's server-returned
 * `AvailabilityPeriodCell[]` onto that axis by date-range OVERLAP — the same
 * proven pattern the offer-draft availability tab uses
 * (`availability-tab-page.tsx`'s `enumeratePeriods` + `availability-tab-grid.ts`'s
 * `buildGridData`/`aggregateCell`), reusing `findOverlappingCells`
 * (`../../lib/period-overlap.ts`) for the overlap matching itself.
 *
 * Fixes the mixed-granularity bug (openspec `client-offer-interactive-view`
 * spec): two units in one offer can resolve to different server-side
 * granularities (`offer-draft-availability` spec), so a shared axis built
 * from the exact union of raw cell start dates falsely rendered a unit's
 * genuinely-free slot as `reserved` whenever its cell boundaries didn't
 * exactly match another unit's. Building the axis independently via
 * `enumeratePeriods` and re-bucketing by overlap removes that false-reserved
 * failure mode entirely.
 */
import {
  enumeratePeriods,
  type AvailabilityPeriodCell,
  type ClientOfferViewUnit,
  type PeriodGranularity,
} from '@ooh/contracts';
import type {
  ClientOfferReviewHalfStatus,
  ClientOfferReviewPeriod,
} from '../../client-offer-review';
import { findOverlappingCells } from '../../../lib/period-overlap';
import { formatPeriodLabel, getCellStateFromPeriod } from './client-offer-availability-helpers';

export interface UnitAvailability {
  id: string;
  availability: AvailabilityPeriodCell[];
}

/** `getCellStateFromPeriod` only ever returns 'free' | 'sold' | 'reserved-other-soft' for client-view data. */
function toHalfStatus(
  cellState: ReturnType<typeof getCellStateFromPeriod>,
): ClientOfferReviewHalfStatus {
  if (cellState === 'sold') return 'sold';
  if (cellState === 'free') return 'free';
  return 'reserved';
}

function toIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/**
 * Builds the shared period axis for the given campaign window and
 * granularity — independent of any unit's own availability data, unlike the
 * old cell-union axis. `period.hix` stays 1:1 with the axis index (no
 * half-month sub-packing — real availability cells are re-bucketed onto
 * whichever granularity is selected via overlap, not pre-split).
 */
export function buildReviewPeriods(
  window: { from: Date; to: Date },
  granularity: PeriodGranularity,
  language = 'pl',
): { periods: ClientOfferReviewPeriod[] } {
  const periods = enumeratePeriods(window, granularity).map((period, ix) => {
    const start = toIsoDate(period.start);
    const end = toIsoDate(period.end);
    return {
      id: `period-${start}`,
      label: formatPeriodLabel({ start, end }, language),
      hix: [ix],
      minWidth: '52px',
      start,
      end,
    };
  });

  return { periods };
}

/**
 * Builds a single unit's `halves` array, aligned index-for-index with
 * `periods`. An axis slot with no overlapping cell at all yields `'reserved'`,
 * never `'free'` — a free default there would produce an `unitDecisions`
 * payload the server rejects. An axis slot overlapping MULTIPLE cells (finer
 * display granularity than the unit's own server-resolved granularity, or a
 * coarser display granularity spanning several server cells) takes the worst
 * status among them: sold > reserved > free.
 */
export function buildUnitHalves(
  unit: UnitAvailability,
  periods: ClientOfferReviewPeriod[],
): ClientOfferReviewHalfStatus[] {
  return periods.map((period) => {
    const overlapping = findOverlappingCells(unit.availability, period);
    if (overlapping.length === 0) return 'reserved';

    let worst: ClientOfferReviewHalfStatus = 'free';
    for (const cellItem of overlapping) {
      const status = toHalfStatus(getCellStateFromPeriod(cellItem));
      if (status === 'sold') return 'sold';
      if (status === 'reserved') worst = 'reserved';
    }
    return worst;
  });
}

/**
 * Derives a unit's default-selected period ids from an already-built `halves`
 * array (periods with status `'free'`) — kept in the SAME axis id space as
 * `periods` itself, so selections stay consistent when granularity changes
 * and re-maps the axis. Also the single source of truth for "can this unit
 * be accepted at all" (a non-empty result means the unit is acceptable).
 */
export function getDefaultPeriodIdsFromHalves(
  periods: ClientOfferReviewPeriod[],
  halves: ClientOfferReviewHalfStatus[],
): string[] {
  return periods.filter((_period, ix) => halves[ix] === 'free').map((period) => period.id);
}

/**
 * Derives a fallback enumeration window from the union of every unit's own
 * `AvailabilityPeriodCell[]` (earliest `start`, latest `end`) — used by
 * `buildReviewPeriodAxis` when the offer has no campaign window configured
 * yet (`offer.defaultDateFrom`/`defaultDateTo` are legitimately nullable at
 * creation). ISO date strings (`YYYY-MM-DD`) sort lexically identical to
 * chronological order, so plain string comparison is sufficient here.
 * Returns `null` when no unit has any cell either.
 */
function deriveFallbackWindow(
  unitAvailabilityById: Map<string, AvailabilityPeriodCell[]>,
): { from: Date; to: Date } | null {
  let minStart: string | null = null;
  let maxEnd: string | null = null;
  for (const cells of unitAvailabilityById.values()) {
    for (const availabilityCell of cells) {
      if (minStart === null || availabilityCell.start < minStart) minStart = availabilityCell.start;
      if (maxEnd === null || availabilityCell.end > maxEnd) maxEnd = availabilityCell.end;
    }
  }
  if (minStart === null || maxEnd === null) return null;
  return { from: new Date(minStart), to: new Date(maxEnd) };
}

/**
 * Builds the shared period axis for a campaign window + granularity AND
 * every unit's `halves` array against that axis in one call — the single
 * implementation shared by `useClientOfferSelections` (accept/period-select
 * default-period axis) and `useClientOfferReviewUnits` (the shell's
 * displayed axis), both of which independently built the SAME axis for the
 * SAME offer on the SAME screen from their own per-unit availability lookup
 * before this extraction.
 *
 * When the campaign window isn't configured yet (`campaignFrom`/`campaignTo`
 * null), falls back to `deriveFallbackWindow` so the axis still enumerates
 * from the units' own availability cells instead of collapsing to an empty
 * axis — an empty axis would make every unit's `canAccept` disagree with
 * what's actually selectable, silently dropping accepted units from the
 * submit payload (see `buildUnitDecisionsPayload`).
 */
export function buildReviewPeriodAxis(
  unitAvailabilityById: Map<string, AvailabilityPeriodCell[]>,
  granularity: PeriodGranularity,
  campaignFrom: Date | null,
  campaignTo: Date | null,
  language = 'pl',
): {
  periods: ClientOfferReviewPeriod[];
  halvesByUnitId: Map<string, ClientOfferReviewHalfStatus[]>;
} {
  const window =
    campaignFrom && campaignTo
      ? { from: campaignFrom, to: campaignTo }
      : deriveFallbackWindow(unitAvailabilityById);
  const { periods } = window ? buildReviewPeriods(window, granularity, language) : { periods: [] };
  const halvesByUnitId = new Map<string, ClientOfferReviewHalfStatus[]>();
  for (const [id, availability] of unitAvailabilityById) {
    halvesByUnitId.set(id, buildUnitHalves({ id, availability }, periods));
  }
  return { periods, halvesByUnitId };
}

/**
 * Adapts an `enrichedById` lookup (snapshot row id -> enriched unit) into
 * `buildReviewPeriodAxis`'s `unitAvailabilityById` shape and calls it — shared by every
 * caller that starts from `enrichedById` rather than a raw availability map (the
 * interactive selections hook and the post-acceptance read-only hydration).
 */
export function buildReviewPeriodAxisFromEnrichedUnits(
  enrichedById: Map<number, ClientOfferViewUnit>,
  granularity: PeriodGranularity,
  campaignFrom: Date | null,
  campaignTo: Date | null,
  language = 'pl',
): {
  periods: ClientOfferReviewPeriod[];
  halvesByUnitId: Map<string, ClientOfferReviewHalfStatus[]>;
} {
  const unitAvailabilityById = new Map<string, AvailabilityPeriodCell[]>();
  for (const [id, unit] of enrichedById) {
    unitAvailabilityById.set(String(id), unit.availability);
  }
  return buildReviewPeriodAxis(
    unitAvailabilityById,
    granularity,
    campaignFrom,
    campaignTo,
    language,
  );
}
